import React, { useState } from "react";
import { ArrowRight, Sparkles, CheckCircle2, MapPin } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { heroImage } from "../mock";
import { trackEvent } from "./Analytics";
import { submitLead } from "../lib/api";

const SuburbHero = ({ suburb, region, blurb }) => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service: "",
    suburb: suburb,
    notes: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const onChange = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.service) {
      toast.error("Please fill in name, phone and service.");
      return;
    }
    const result = await submitLead({
      source: "suburb",
      suburb_page: suburb.toLowerCase().replace(/\s+/g, "-"),
      ...form,
    });
    if (result.ok) {
      toast.success(`Quote request received — we’ll be in touch within 24 hours.`);
    } else {
      toast.success(`Got your request — we’ll be in touch shortly.`);
    }
    trackEvent("lead_submit", {
      source: "suburb_form",
      service: form.service,
      suburb: suburb,
    });
    setSubmitted(true);
  };

  return (
    <section
      id="home"
      className="relative min-h-[100vh] pt-28 pb-16 lg:pt-32 lg:pb-24 overflow-hidden"
    >
      <div className="absolute inset-0 z-0">
        <img
          src={heroImage}
          alt={`Premium cleaning in ${suburb}`}
          className="w-full h-full object-cover [transform:scaleX(-1)]"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#fdfbf7]/15 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#fdfbf7]/10 via-transparent to-transparent" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* Left: copy */}
        <div className="max-w-xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/70 backdrop-blur border border-[#e8d9cc] text-xs uppercase tracking-[0.2em] text-[#daae9c] fade-up">
            <MapPin size={14} className="text-sterling-dark" />
            Premium Cleaning · {suburb}, Brisbane
          </div>
          <h1
            className="font-serif-display text-5xl md:text-6xl lg:text-7xl text-white mt-6 leading-[1.02] text-balance fade-up-delay-1"
            style={{ textShadow: "0 2px 24px rgba(58, 38, 26, 0.45)" }}
          >
            Premium cleaning in{" "}
            <span className="italic font-bold text-[#daae9c]">{suburb}.</span>
          </h1>
          <p className="mt-6 text-lg text-ink/70 leading-relaxed fade-up-delay-2">
            {blurb}{" "}
            Tailored, discreet and meticulous — a refined cleaning service for
            homes in {suburb} and the surrounding {region}.
          </p>

          <div className="mt-8 flex flex-wrap gap-3 fade-up-delay-3">
            <a href="#services">
              <Button
                variant="outline"
                className="rounded-full bg-white/90 border-[#daae9c] text-ink hover:bg-[#daae9c] hover:text-ink px-6 h-12 transition-colors"
              >
                Explore packages
              </Button>
            </a>
            <a href="#quote-form">
              <Button className="rounded-full bg-[#daae9c] hover:bg-white text-ink border border-transparent hover:border-[#daae9c] transition-colors px-6 h-12 group">
                Book your {suburb} clean
                <ArrowRight
                  size={16}
                  className="ml-2 group-hover:translate-x-1 transition-transform"
                />
              </Button>
            </a>
          </div>

          <div className="mt-10 flex items-center gap-6 text-sm text-ink/60 fade-up-delay-3">
            <div className="flex items-center gap-2">
              <CheckCircle2 size={16} className="text-sterling-dark" />
              Servicing {suburb}
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 size={16} className="text-sterling-dark" />
              Fully insured
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 size={16} className="text-sterling-dark" />
              Police checked
            </div>
          </div>
        </div>

        {/* Right: form card */}
        <div id="quote-form" className="relative fade-up-delay-2">
          <div className="absolute -inset-1 rounded-3xl bg-sterling/30 blur-2xl" />
          <form
            onSubmit={onSubmit}
            className="relative bg-white/90 backdrop-blur-xl border border-[#ead8c9] rounded-3xl p-8 lg:p-10 shadow-[0_20px_60px_-20px_rgba(138,93,72,0.25)]"
          >
            {!submitted ? (
              <>
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#daae9c]">
                  <span className="w-6 h-px bg-sterling-dark" />
                  Quote for {suburb}
                </div>
                <h3 className="font-serif-display text-3xl text-ink mt-3">
                  Tell us about your {suburb} home
                </h3>
                <p className="text-sm text-ink/60 mt-2">
                  We respond within 24 hours with a tailored quote.
                </p>

                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="text-xs text-ink/70 uppercase tracking-wider">
                      Full name
                    </label>
                    <Input
                      value={form.name}
                      onChange={onChange("name")}
                      placeholder="Jane Doe"
                      className="mt-1.5 h-11 bg-white border-[#e8d9cc] focus-visible:ring-sterling rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-ink/70 uppercase tracking-wider">
                      Phone
                    </label>
                    <Input
                      value={form.phone}
                      onChange={onChange("phone")}
                      placeholder="04xx xxx xxx"
                      className="mt-1.5 h-11 bg-white border-[#e8d9cc] focus-visible:ring-sterling rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-ink/70 uppercase tracking-wider">
                      Email
                    </label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={onChange("email")}
                      placeholder="you@email.com"
                      className="mt-1.5 h-11 bg-white border-[#e8d9cc] focus-visible:ring-sterling rounded-lg"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-xs text-ink/70 uppercase tracking-wider">
                      Service
                    </label>
                    <Select
                      value={form.service}
                      onValueChange={(v) =>
                        setForm((f) => ({ ...f, service: v }))
                      }
                    >
                      <SelectTrigger className="mt-1.5 h-11 bg-white border-[#e8d9cc] rounded-lg">
                        <SelectValue placeholder="Choose" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="express-reset">Express Reset</SelectItem>
                        <SelectItem value="sanctuary-reset">Sanctuary Reset</SelectItem>
                        <SelectItem value="presentation-clean">Presentation Clean</SelectItem>
                        <SelectItem value="deep-clean">Deep Clean</SelectItem>
                        <SelectItem value="bond-clean">Bond Clean</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-xs text-ink/70 uppercase tracking-wider">
                      Additional information
                      <span className="text-ink/40 normal-case tracking-normal ml-1">
                        (optional)
                      </span>
                    </label>
                    <Textarea
                      value={form.notes}
                      onChange={onChange("notes")}
                      rows={3}
                      placeholder={`Tell us about your ${suburb} home — specific areas, access details or anything we should know.`}
                      className="mt-1.5 bg-white border-[#e8d9cc] focus-visible:ring-sterling rounded-lg resize-none"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="mt-6 w-full h-12 bg-[#daae9c] hover:bg-white text-ink border border-transparent hover:border-[#daae9c] transition-colors rounded-full text-sm tracking-wide"
                >
                  Request my quote
                  <ArrowRight size={16} className="ml-2" />
                </Button>
                <p className="text-[11px] text-ink/50 mt-3 text-center">
                  By submitting, you agree to be contacted about your enquiry.
                </p>
              </>
            ) : (
              <div className="text-center py-6">
                <div className="mx-auto w-14 h-14 rounded-full bg-sterling/30 flex items-center justify-center">
                  <CheckCircle2 className="text-[#daae9c]" size={28} />
                </div>
                <h3 className="font-serif-display text-3xl text-ink mt-4">
                  Thank you, {form.name.split(" ")[0]}.
                </h3>
                <p className="text-ink/70 mt-2">
                  Your {suburb} quote request is in. We’ll be in touch within 24 hours.
                </p>
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

export default SuburbHero;
